// src/components/layout/Sidebar/Sidebar.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faBook,
	faBookOpen,
	faCalendarAlt,
	faBars,
	faTimes,
	faSignOutAlt,
	faChartLine,
	faChalkboardTeacher,
	faPeopleGroup,
	faUserCheck,
} from "@fortawesome/free-solid-svg-icons";
import SidebarButton from "../../layout/Sidebar/components/SidebarButton/SidebarButton";
import { PRIVATE_ROUTES } from "../../../config/routes";
import { logOut } from "../../../store/slices/authSlice";
import { selectCurrentUser } from "../../../store/slices/authSlice";
import {
	useGetUserSubjectsQuery,
	useGetUserDepartmentsQuery,
	useGetUserClassDepartmentsQuery,
	useGetDepartmentsQuery,
} from "../../../store/api";
import styles from "./Sidebar.module.css";

const Sidebar = ({ isOpen, toggleLeftSidebar, toggleRightSidebar }) => {
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
	const [activeItem, setActiveItem] = useState("");
	const dispatch = useDispatch();
	const location = useLocation();
	const user = useSelector(selectCurrentUser);

	const {
		data: subjectsData = [],
		isLoading: isLoadingSubjects,
		error: subjectsError,
	} = useGetUserSubjectsQuery(undefined, {
		skip: !user,
	});

	// Fetch user's departments
	const {
		data: departmentsData = [],
		isLoading: isLoadingDepartments,
		error: departmentsError,
	} = useGetUserDepartmentsQuery(undefined, {
		skip: !user,
	});

	// Fetch class teacher departments (departments where user is class teacher)
	const {
		data: classDepartments = [],
		isLoading: isLoadingClassDepartments,
		error: classDepartmentsError,
	} = useGetUserClassDepartmentsQuery(undefined, {
		skip: !user,
	});

	// Fetch all departments (for admins)
	const {
		data: allDepartments = [],
		isLoading: isLoadingAllDepartments,
		error: allDepartmentsError,
	} = useGetDepartmentsQuery(undefined, {
		skip: user?.role !== "Admin",
	});

	// Log errors for debugging
	useEffect(() => {
		if (subjectsError) {
			console.error("Error fetching subjects:", subjectsError);
		}
		if (departmentsError) {
			console.error("Error fetching departments:", departmentsError);
		}
		if (classDepartmentsError) {
			console.error("Error fetching class departments:", classDepartmentsError);
		}
		if (allDepartmentsError) {
			console.error("Error fetching all departments:", allDepartmentsError);
		}
	}, [
		subjectsError,
		departmentsError,
		classDepartmentsError,
		allDepartmentsError,
	]);

	// Function to extract number from department name for sorting
	const extractNumberFromDepartmentName = (deptName) => {
		// Extract numbers from strings like "1", "1a", "Class 1A", "10B", etc.
		const match = deptName.match(/(\d+)/);
		return match ? parseInt(match[1], 10) : 999; // 999 for departments without numbers (they go to the end)
	};

	// Function to sort departments numerically
	const sortDepartmentsNumerically = (departments) => {
		return [...departments].sort((a, b) => {
			const numA = extractNumberFromDepartmentName(a.departmentName);
			const numB = extractNumberFromDepartmentName(b.departmentName);

			if (numA !== numB) {
				return numA - numB; // Sort by number first
			}

			// If numbers are the same, sort alphabetically
			return a.departmentName.localeCompare(b.departmentName);
		});
	};

	// Format grade title
	const formatGradeTitle = (deptName) => {
		return `${deptName}`;
	};

	// Handle window resize for mobile view
	useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth <= 768);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	// Prepare department routes for grades
	const myGrades = useMemo(() => {
		const isAdmin = user?.role === "Admin";
		const isClassTeacher = classDepartments && classDepartments.length > 0;

		let gradesDepartments = [];

		if (isAdmin) {
			// Admins get access to all departments
			gradesDepartments = allDepartments;
		} else {
			// Regular teachers get their assigned departments
			gradesDepartments = [...departmentsData];

			// Class teachers also get access to their class departments
			if (isClassTeacher) {
				// Add class teacher departments, avoiding duplicates
				const existingDeptIds = new Set(departmentsData.map((dept) => dept.id));
				classDepartments.forEach((classDept) => {
					if (!existingDeptIds.has(classDept.id)) {
						gradesDepartments.push(classDept);
					}
				});
			}
		}

		// Sort departments numerically
		const sortedDepartments = sortDepartmentsNumerically(gradesDepartments);

		return sortedDepartments.map((dept) => ({
			title: formatGradeTitle(dept.departmentName),
			path: `/grades/${dept.id}`,
		}));
	}, [departmentsData, classDepartments, allDepartments, user]);

	// Prepare department routes for assessments
	const myAssessments = useMemo(() => {
		// Sort departments numerically
		const sortedDepartments = sortDepartmentsNumerically(departmentsData);

		return sortedDepartments.map((dept) => ({
			title: formatGradeTitle(dept.departmentName),
			path: `/assessments/${dept.id}`,
		}));
	}, [departmentsData]);

	// Prepare department routes for lectures (admin only)
	const myDepartments = useMemo(() => {
		// Sort departments numerically
		const sortedDepartments = sortDepartmentsNumerically(departmentsData);

		return sortedDepartments.map((dept) => ({
			title: formatGradeTitle(dept.departmentName),
			path: `/lectures/${dept.id}`,
		}));
	}, [departmentsData]);

	// Prepare attendance routes based on user role
	const attendanceRoutes = useMemo(() => {
		const isAdmin = user?.role === "Admin";
		const isClassTeacher = classDepartments && classDepartments.length > 0;

		if (isAdmin) {
			// Admins see all departments, sorted numerically
			const sortedDepartments = sortDepartmentsNumerically(allDepartments);
			return sortedDepartments.map((dept) => ({
				title: formatGradeTitle(dept.departmentName),
				path: `/attendance/${dept.id}`,
			}));
		} else if (isClassTeacher) {
			// Class teachers see only their assigned departments, sorted numerically
			const sortedDepartments = sortDepartmentsNumerically(classDepartments);

			if (sortedDepartments.length === 1) {
				// If only one department, return direct route
				return `/attendance/${sortedDepartments[0].id}`;
			} else {
				// If multiple departments, return array of routes
				return sortedDepartments.map((dept) => ({
					title: formatGradeTitle(dept.departmentName),
					path: `/attendance/${dept.id}`,
				}));
			}
		}

		// Regular teachers don't get attendance routes
		return null;
	}, [user, classDepartments, allDepartments]);

	// Build menu items
	const menuItems = useMemo(() => {
		const items = [];
		const isAdmin = user?.role === "Admin";
		const isClassTeacher = classDepartments && classDepartments.length > 0;

		// Common items for all teachers
		items.push(
			{
				title: "Assessments",
				icon: faBookOpen,
				route: myAssessments,
			},
			{
				title: "Grades",
				icon: faBook,
				route: myGrades,
			},
			{
				title: "Weekly Report",
				icon: faChartLine,
				route: PRIVATE_ROUTES.LOGS,
			}
		);

		// Add attendance for class teachers and admins only
		if (isAdmin || isClassTeacher) {
			items.push({
				title: "Attendance",
				icon: faUserCheck,
				route: attendanceRoutes,
			});
		}

		// Admin-only items
		if (isAdmin) {
			items.push(
				{
					title: "Lectures",
					icon: faBookOpen,
					route: myDepartments,
				},
				{
					title: "Teachers",
					icon: faChalkboardTeacher,
					route: PRIVATE_ROUTES.TEACHERS,
				},
				// DEPRECATED SUBJECTS MENU ITEM
				// {
				// 	title: "Subjects",
				// 	icon: faBook,
				// 	route: PRIVATE_ROUTES.SUBJECTS,
				// },
				{
					title: "Classes",
					icon: faPeopleGroup,
					route: PRIVATE_ROUTES.CLASSES,
				},
				{
					title: "Parents",
					icon: faPeopleGroup,
					route: PRIVATE_ROUTES.PARENTS,
				},
				{
					title: "Students",
					icon: faPeopleGroup,
					route: PRIVATE_ROUTES.STUDENTS,
				}
			);
		}

		return items;
	}, [
		user,
		myGrades,
		myAssessments,
		myDepartments,
		classDepartments,
		attendanceRoutes,
	]);

	// Set active item based on current route
	useEffect(() => {
		const activeMenuItem = menuItems.find((item) => {
			if (!item.route) return false;

			if (Array.isArray(item.route)) {
				return item.route.some((sub) => sub.path === location.pathname);
			} else if (typeof item.route === "string") {
				// Handle direct routes
				const routePattern = item.route.replace(/:[^/]+/g, "[^/]+");
				const regex = new RegExp(`^${routePattern}$`);
				return regex.test(location.pathname);
			}
			return false;
		});

		if (activeMenuItem) {
			setActiveItem(activeMenuItem.title);
		}
	}, [location.pathname, menuItems]);

	const handleButtonClick = (title) => {
		setActiveItem(title);
		if (isMobile) {
			toggleLeftSidebar();
		}
	};

	const handleLogout = () => {
		dispatch(logOut());
		if (isMobile) {
			toggleLeftSidebar();
		}
	};

	// Don't render attendance items if user is loading
	if (
		isLoadingClassDepartments ||
		(user?.role === "Admin" && isLoadingAllDepartments)
	) {
		// You could show a loading state here if needed
	}

	return (
		<>
			{isMobile && (
				<div className={styles.topbar}>
					<div className={styles.topbarLeft}>
						<FontAwesomeIcon
							icon={isOpen ? faTimes : faBars}
							className={styles.hamburgerIcon}
							onClick={toggleLeftSidebar}
						/>
					</div>
					<div className={styles.topbarRight}>
						<FontAwesomeIcon
							icon={faCalendarAlt}
							className={styles.calendarIcon}
							onClick={toggleRightSidebar}
						/>
					</div>
				</div>
			)}

			<div className={`${styles.sidebar} ${isOpen ? styles.open : ""}`}>
				<div className={styles.sidebarMenu}>
					{menuItems.map((item) => {
						// Skip items with null routes (e.g., attendance for regular teachers)
						if (item.route === null) return null;

						return (
							<SidebarButton
								key={item.title}
								title={item.title}
								icon={item.icon}
								route={item.route}
								isActive={activeItem === item.title}
								onClick={() => handleButtonClick(item.title)}
							/>
						);
					})}
				</div>

				<div className={styles.logoutContainer}>
					<SidebarButton
						title="Logout"
						icon={faSignOutAlt}
						route="/login"
						isActive={false}
						onClick={handleLogout}
					/>
				</div>
			</div>
		</>
	);
};

export default Sidebar;
